/**
 * Aba "Encaminhar" do TaskDetailModal.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Forward } from 'lucide-react';
import { useState } from 'react';

import { apiClient, type MembershipWithUserResponse } from '@/lib/api-client';

export interface ForwardTabProps {
  taskId: string;
  tenantId: string;
  /** IDs dos responsáveis atuais para excluir do dropdown */
  currentAssigneeUserIds?: string[];
}

export function ForwardTab({ taskId, tenantId, currentAssigneeUserIds = [] }: ForwardTabProps) {
  const queryClient = useQueryClient();
  const [toUserId, setToUserId] = useState('');
  const [note, setNote] = useState('');
  const [lastForwarded, setLastForwarded] = useState<{ name: string; at: string } | null>(null);

  const { data: membersData = [] } = useQuery<MembershipWithUserResponse[]>({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => apiClient.tenantsListMembers(tenantId),
    staleTime: 5 * 60_000,
    enabled: !!tenantId,
  });

  const availableMembers = membersData.filter(
    (m) => !currentAssigneeUserIds.includes(m.userId),
  );

  const forwardMut = useMutation({
    mutationFn: () => apiClient.tasksForward(taskId, { toUserId, note }),
    onSuccess: (data) => {
      const memberName =
        membersData.find((m) => m.userId === toUserId)?.user.name ?? 'Usuário';
      setLastForwarded({ name: memberName, at: new Date().toLocaleString('pt-BR') });
      setToUserId('');
      setNote('');
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId, 'comments'] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toUserId || forwardMut.isPending) return;
    forwardMut.mutate();
  }

  return (
    <div className="flex flex-col gap-4 px-5 py-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {/* Para */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--fg-muted)]">Para</label>
          <select
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            required
            className="rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          >
            <option value="">Selecionar membro…</option>
            {availableMembers.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.user.name}
              </option>
            ))}
          </select>
          {availableMembers.length === 0 && (
            <p className="text-[11px] text-[var(--fg-muted)]">
              Nenhum membro disponível para encaminhar.
            </p>
          )}
        </div>

        {/* Nota */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-[var(--fg-muted)]">
            Nota (opcional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Adicione uma observação para o destinatário…"
            className="w-full resize-none rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
        </div>

        {/* Erro */}
        {forwardMut.isError && (
          <p className="text-xs text-red-600">
            Falha ao encaminhar tarefa. Tente novamente.
          </p>
        )}

        <div className="flex items-center justify-between">
          {lastForwarded ? (
            <p className="flex items-center gap-1.5 text-xs text-green-600">
              <Forward className="h-3.5 w-3.5" />
              Encaminhado para {lastForwarded.name} em {lastForwarded.at}
            </p>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!toUserId || forwardMut.isPending}
            className="rounded-md bg-[var(--accent)] px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40 transition"
          >
            {forwardMut.isPending ? 'Encaminhando…' : 'Encaminhar'}
          </button>
        </div>
      </form>
    </div>
  );
}
